
import { ChevronDownIcon, XCircleFillIcon } from '@primer/octicons-react';
import { Box, Button, SelectPanel } from '@primer/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { hocSinhApi } from '../../api/hocSinhApi';
import { useCommonContext } from '../../contexts/common';
import { useCommonSelectedHook } from '../../hooks/useCommonSelectedHook';
import { IHocSinhModel } from '../../models/response/hoc-sinh/IHocSinhModel';
import UserAvatar from '../user-avatar/UserAvatar';
type ComboboxHocSinhProps = {
    isReadonly?: boolean;
    value?: number;
    onValueChanged: (id: number, data?: IHocSinhModel) => void;
    className?: string;
    preText?: string;
    width?: string | number;
    stylingMode?: 'outlined' | 'filled' | 'underlined';
    isShowClearBtn?: boolean,
    maxWidth?: any,
    // Filter props
    filterTruongId?: number;
    filterKhoiId?: number;
    filterHeId?: number;
};
const SelectBoxHocSinhNew = (props: ComboboxHocSinhProps) => {
    const { translate } = useCommonContext();
    const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
    const [allHocSinhs, setAllHocSinhs] = useState<IHocSinhModel[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadAllHocSinh = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await hocSinhApi.SelectViewShort({
                nam_hoc: nam_hoc,
                dm_coso_id: dm_coso_id,
                searchText: '', // Load all students
                dm_trangthaihocsinh_ids: [],
                dm_lop_id: 0,
                dm_truong_id: props.filterTruongId || 0,
                dm_he_id: props.filterHeId || 0,
                dm_khoi_id: props.filterKhoiId || 0
            });
            if (res.is_success) {
                setAllHocSinhs(res.data);
            }
        } catch (error) {
            console.error('Error loading students:', error);
            setAllHocSinhs([]);
        } finally {
            setIsLoading(false);
        }
    }, [nam_hoc, dm_coso_id]);

    // Load all students when component mounts or when nam_hoc/dm_coso_id changes
    useEffect(() => {
        loadAllHocSinh();
    }, [loadAllHocSinh]);

    const hocSinhFilters = useMemo(() => {
        return allHocSinhs.filter((x) => {
            return (!props.filterTruongId || x.dm_truong_id === props.filterTruongId) &&
                   (!props.filterKhoiId || x.dm_khoi_id === props.filterKhoiId) &&
                   (!props.filterHeId || x.dm_he_id === props.filterHeId);
        });
    }, [allHocSinhs, props.filterTruongId, props.filterKhoiId, props.filterHeId]);

    const [filter, setFilter] = useState('')
    const [open, setOpen] = useState(false)

    // Remove Vietnamese tones for better search
    const removeVietnameseTones = (str: string) => {
        return str
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/đ/g, "d")
            .replace(/Đ/g, "D");
    };

    // Filter students on client side
    const filteredHocSinhs = useMemo(() => {
        if (!filter || filter.trim().length < 2) {
            return [];
        }

        const normalizedFilter = removeVietnameseTones(filter.toLowerCase());
        return hocSinhFilters.filter(hs => {
            const normalizedName = removeVietnameseTones(hs.ho_ten?.toLowerCase() || '');
            const normalizedCode = removeVietnameseTones(hs.ma_hs?.toLowerCase() || '');

            return normalizedName.includes(normalizedFilter) ||
                   normalizedCode.includes(normalizedFilter);
        });
    }, [hocSinhFilters, filter]);

    const dataSource = useMemo(() => {
        if (isLoading) {
            return [{
                id: -1,
                text: 'Đang tải dữ liệu...',
                disabled: true
            }];
        }

        // if (!filter || filter.trim().length < 2) {
        //     return [{
        //         id: -3,
        //         text: 'Nhập ít nhất 2 ký tự để tìm kiếm',
        //         disabled: true
        //     }];
        // }

        if (filter && filter.trim().length >= 2 && filteredHocSinhs.length === 0) {
            return [{
                id: -2,
                text: 'Không tìm thấy học sinh nào',
                disabled: true
            }];
        }

        // Chỉ lấy top 20 học sinh để tối ưu hiệu suất
        const top20Students = filteredHocSinhs.slice(0, 20);
        const hasMore = filteredHocSinhs.length > 20;

        const items = top20Students.map((x: IHocSinhModel) => {
            const item: any = {
                id: x.id,
                text: x.ma_hs + ` - ` + x.ho_ten + (x.ten_khoi != null ? (` - ` + x.ten_khoi) : '') + (x.ten_lop != null ? (` - ` + x.ten_lop) : ''),
                leadingVisual: () => <UserAvatar fullName={x.ho_ten} size="small" />
            }
            return item;
        });

        // Thêm thông báo nếu có nhiều hơn 20 kết quả
        if (hasMore) {
            items.push({
                id: -4,
                text: `Hiển thị 20/${filteredHocSinhs.length} kết quả. Nhập thêm từ khóa để thu hẹp tìm kiếm.`,
                disabled: true
            });
        }

        return items;
    }, [filteredHocSinhs, isLoading, filter]);

    const _selectedData = useMemo(() => {
        return dataSource.find((item: any) => item.id === props.value)
    }, [props.value, dataSource])
    return (
        <SelectPanel
            renderAnchor={({ children, 'aria-labelledby': ariaLabelledBy, ...anchorProps }) => {
                const selectedStudent = allHocSinhs.find((hs: IHocSinhModel) => hs.id === props.value);
                return (
                    <Button sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }} trailingAction={ChevronDownIcon} aria-labelledby={` ${ariaLabelledBy}`} {...anchorProps}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, maxWidth: props.maxWidth, overflow: "hidden" }}>
                            {selectedStudent && (
                                <UserAvatar fullName={selectedStudent.ho_ten} size="small" />
                            )}
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {children || translate(`Chọn học sinh`)}
                            </span>
                        </Box>
                    </Button>
                );
            }}
            title={<>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box sx={{ flex: 1 }}>
                        Chọn học sinh
                    </Box>
                    {props.isShowClearBtn && (props.value ?? 0) > 0 &&
                        <Button
                            trailingVisual={XCircleFillIcon}
                            variant='invisible'
                            sx={{
                                color: "danger.emphasis"
                            }}
                            onClick={() => {
                                props.onValueChanged(0)
                            }}
                        >
                            Bỏ chọn
                        </Button>
                    }
                </Box>
            </>}
            placeholderText="Nhập tên hoặc mã học sinh để tìm kiếm (tối thiểu 2 ký tự)"
            open={props.isReadonly ? false : open}

            onOpenChange={setOpen}
            items={dataSource}
            selected={_selectedData}
            onSelectedChange={(data: any) => {
                // Don't allow selection of disabled items (loading/no results)
                if (data.id <= 0) return;
                props.onValueChanged(data.id, allHocSinhs.find((x: IHocSinhModel) => x.id === data.id))
            }}
            onFilterChange={setFilter}
            showItemDividers={true}
            overlayProps={{ width: 'large', height: 'medium' }}
        />

    );
};

export default SelectBoxHocSinhNew;
